import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LifeBuoy, RefreshCw, Sparkles, MessageCircle, Mail, Smartphone } from 'lucide-react';
import { AppStoreButtons } from '@/components/mobile/AppStoreButtons';
import { resetTour } from '@/components/onboarding/CoachMark';
import { ALL_TOUR_IDS } from '@/lib/tours';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const FAQ_KEYS = [
  'gettingStarted',
  'firstShift',
  'inviteCaregiver',
  'vitalRanges',
  'subscriptions',
  'companyVsFamily',
  'notifications',
  'privacy',
] as const;

const SUPPORT_WHATSAPP = (import.meta.env.VITE_SUPPORT_WHATSAPP || '').replace(/[^\d+]/g, '');
const SUPPORT_EMAIL = import.meta.env.VITE_SUPPORT_EMAIL || 'help@careos.life';

export default function HelpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const restartAllTours = async () => {
    ALL_TOUR_IDS.forEach((id) => resetTour(id));
    // Clear server-side progress too, so tours restart on every device.
    if (user) {
      await supabase.from('user_tour_progress').delete().eq('user_id', user.id);
    }
    toast.success(t('help.toursReset'));
    navigate('/app');
  };

  return (
    <AppLayout title={t('help.title')} showBack onBack={() => navigate(-1)} showFAB={false}>
      <div className="p-4 max-w-3xl mx-auto space-y-4">
        {/* Hero */}
        <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
              <LifeBuoy className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold">{t('help.heroTitle')}</h2>
              <p className="text-sm text-muted-foreground">{t('help.heroDesc')}</p>
            </div>
          </CardContent>
        </Card>

        {/* Restart tours */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              {t('help.guidedTours')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('help.guidedToursDesc')}</p>
            <Button variant="outline" size="sm" onClick={restartAllTours}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('help.restartTours')}
            </Button>
          </CardContent>
        </Card>

        {/* App download */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-primary" />
              {t('appDownload.helpSectionTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('appDownload.helpSectionDesc')}</p>
            <AppStoreButtons />
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('help.faq')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {FAQ_KEYS.map((key) => (
                <AccordionItem key={key} value={key}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {t(`help.items.${key}.q`)}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground whitespace-pre-line">
                    {t(`help.items.${key}.a`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* Contact */}
        {(SUPPORT_WHATSAPP || SUPPORT_EMAIL) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('help.stillNeedHelp')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {SUPPORT_WHATSAPP && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    window.open(
                      `https://wa.me/${SUPPORT_WHATSAPP.replace(/^\+/, '')}`,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {t('support.whatsapp')}
                </Button>
              )}
              {SUPPORT_EMAIL && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    window.location.href = `mailto:${SUPPORT_EMAIL}`;
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {t('support.email')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
